/*
  Watchlist page.

  Reads the saved handles from localStorage, then fetches each product live
  from /products/<handle>.js so price, image and availability are current
  rather than a stale snapshot taken when the item was saved.

  Products that 404 (deleted, unpublished) are dropped from storage silently —
  a watchlist that shows items you can never buy is worse than a shorter one.
*/
(function () {
  var WATCH_KEY = 'savendo:watchlist';

  function read() {
    try {
      var raw = window.localStorage.getItem(WATCH_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(WATCH_KEY, JSON.stringify(list));
      document.dispatchEvent(new CustomEvent('savendo:watchlist:change', { detail: { count: list.length } }));
      return true;
    } catch (error) {
      return false;
    }
  }

  function remove(id) {
    var next = read().filter(function (item) {
      return String(item.id) !== String(id);
    });
    write(next);
    return next;
  }

  function money(cents, currency, locale) {
    try {
      return new Intl.NumberFormat(locale || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return '$' + ((Number(cents) || 0) / 100).toFixed(2);
    }
  }

  function card(product, saved, currency, locale) {
    var el = document.createElement('div');
    el.className = 'sv-wl__card';
    el.dataset.id = saved.id;

    var img = product.featured_image
      ? '<img src="' + product.featured_image + '&width=400" alt="" loading="lazy" width="200" height="200">'
      : '<div class="sv-wl__noimg" aria-hidden="true"></div>';

    var status = product.available
      ? '<span class="sv-wl__stock sv-wl__stock--in">In stock</span>'
      : '<span class="sv-wl__stock sv-wl__stock--out">Sold out</span>';

    var compare =
      product.compare_at_price && product.compare_at_price > product.price
        ? ' <s class="sv-wl__was">' + money(product.compare_at_price, currency, locale) + '</s>'
        : '';

    el.innerHTML =
      '<a class="sv-wl__media" href="' + product.url + '">' + img + '</a>' +
      '<div class="sv-wl__body">' +
      '<a class="sv-wl__title" href="' + product.url + '">' + product.title + '</a>' +
      '<p class="sv-wl__price">' + money(product.price, currency, locale) + compare + '</p>' +
      status +
      '<div class="sv-wl__actions">' +
      '<a class="button button--small sv-wl__view" href="' + product.url + '">View item</a>' +
      '<button type="button" class="sv-wl__remove" data-sv-wl-remove aria-label="Remove ' +
      product.title.replace(/"/g, '&quot;') +
      ' from your watchlist">Remove</button>' +
      '</div>' +
      '</div>';

    return el;
  }

  function init() {
    var root = document.querySelector('[data-sv-watchlist]');
    if (!root || root.dataset.svBound === 'true') return;
    root.dataset.svBound = 'true';

    var grid = root.querySelector('[data-sv-wl-grid]');
    var empty = root.querySelector('[data-sv-wl-empty]');
    var loading = root.querySelector('[data-sv-wl-loading]');
    var sync = root.querySelector('[data-sv-wl-sync]');
    var currency = root.dataset.currency;
    var locale = root.dataset.locale;

    function showEmpty() {
      loading.hidden = true;
      grid.hidden = true;
      empty.hidden = false;
      sync.hidden = false; // status is worth showing even with nothing saved
    }

    var saved = read().sort(function (a, b) {
      return (b.at || 0) - (a.at || 0); // most recently saved first
    });

    if (!saved.length) {
      showEmpty();
      return;
    }

    Promise.all(
      saved.map(function (item) {
        if (!item.handle) return Promise.resolve(null);
        return fetch('/products/' + encodeURIComponent(item.handle) + '.js', {
          headers: { accept: 'application/json' },
        })
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .then(function (product) {
            return product ? { product: product, saved: item } : { product: null, saved: item };
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (results) {
      var alive = [];
      var stale = [];

      results.forEach(function (result) {
        if (!result) return;
        if (result.product) alive.push(result);
        else stale.push(result.saved.id);
      });

      // Prune anything that no longer resolves.
      if (stale.length) {
        write(
          read().filter(function (item) {
            return stale.indexOf(item.id) === -1;
          })
        );
      }

      loading.hidden = true;

      if (!alive.length) {
        showEmpty();
        return;
      }

      alive.forEach(function (entry) {
        grid.appendChild(card(entry.product, entry.saved, currency, locale));
      });
      grid.hidden = false;
      sync.hidden = false;
      empty.hidden = true;
    });

    grid.addEventListener('click', function (event) {
      var button = event.target.closest('[data-sv-wl-remove]');
      if (!button) return;
      var el = button.closest('.sv-wl__card');
      if (!el) return;
      var left = remove(el.dataset.id);
      el.remove();
      if (!left.length) showEmpty();
    });
  }

  /*
    When signed in, savendo-watchlist-badge.js merges the local and stored
    lists on load. Rendering before that lands would paint the pre-merge list
    and hide items saved on another device, so wait for the synced event —
    with a timeout so a slow or offline worker never blocks the page.
  */
  function start() {
    var link = document.querySelector('[data-sv-wl-link]');
    var willSync = link && link.dataset.svCustomer && link.dataset.svEndpoint;
    if (!willSync) return init();

    var started = false;
    function go() {
      if (started) return;
      started = true;
      init();
    }
    document.addEventListener('savendo:watchlist:synced', go);
    window.setTimeout(go, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', init);
})();
